import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrossDockOrchestration, CrossDockOrchestrationLeg } from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

@Component({
  selector: 'app-transport-cross-dock-orchestration-view',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-cross-dock-orchestration-view.component.html',
  styleUrls: ['./transport-cross-dock-orchestration-view.component.css']
})
export class TransportCrossDockOrchestrationViewComponent implements OnInit {
  orchestration?: CrossDockOrchestration;
  isLoading = false;
  message = '';

  constructor(
    private route: ActivatedRoute,
    private service: TransportPlanningService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = Number(params.get('crossDockOrchestrationId'));
      if (!Number.isFinite(id) || id <= 0) {
        this.message = 'Invalid cross-dock orchestration.';
        return;
      }
      this.load(id);
    });
  }

  load(id: number): void {
    this.isLoading = true;
    this.message = '';
    this.service.getCrossDockOrchestration(id).subscribe({
      next: row => {
        this.orchestration = row;
        this.isLoading = false;
      },
      error: () => {
        this.message = 'Could not load cross-dock orchestration.';
        this.orchestration = undefined;
        this.isLoading = false;
      }
    });
  }

  metrics(): Array<{ label: string; value: string; hint?: string; tone?: 'success' | 'warn' | 'info' | 'danger' }> {
    const row = this.orchestration;
    return [
      { label: 'Status', value: row?.status || 'PLANNED', hint: row?.validationMessage || row?.validationStatus || 'Ready', tone: this.metricTone(row?.status) },
      { label: 'Legs', value: String(row?.legCount || this.legs().length || 0), hint: 'Generated movement chain', tone: 'info' },
      { label: 'Parent Release', value: row?.parentReleaseId ? `#${row.parentReleaseId}` : '-', hint: row?.sourceNumber || row?.sourceId || '' },
      { label: 'Source WH', value: row?.sourceWarehouseId ? `#${row.sourceWarehouseId}` : '-', hint: row?.sourceType || '' },
      { label: 'Destination WH', value: row?.destinationWarehouseId ? `#${row.destinationWarehouseId}` : '-', hint: 'Final company warehouse' }
    ];
  }

  legs(): CrossDockOrchestrationLeg[] {
    return this.orchestration?.legs ?? [];
  }

  date(value?: string | Date): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : '-';
  }

  severity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const value = (status || '').toUpperCase();
    if (value.includes('COMPLETE') || value.includes('RECEIVED') || value.includes('DISPATCHED')) return 'success';
    if (value.includes('BLOCK') || value.includes('FAIL') || value.includes('ERROR') || value.includes('CANCEL')) return 'danger';
    if (value.includes('PROGRESS') || value.includes('RESERVED') || value.includes('LOADED')) return 'info';
    if (value.includes('MIGRATED') || value.includes('PLANNED')) return 'warn';
    return 'secondary';
  }

  statusLabel(leg: CrossDockOrchestrationLeg): string {
    const status = leg.status || 'PLANNED';
    const reason = (leg.blockedReason || '').toLowerCase();
    if (status.toUpperCase().includes('WAITING_PREVIOUS_LEG') && reason.includes('warehouse receipt')) {
      return 'Waiting for warehouse receipt';
    }
    if (status.toUpperCase().includes('WAITING_PREVIOUS_LEG')) {
      return 'Waiting previous leg';
    }
    return status;
  }

  planningLabel(leg: CrossDockOrchestrationLeg): string {
    const status = (leg.planningStatus || '').toUpperCase();
    const awaitingReceipt = !!leg.gatedByTransferId && leg.gatedTransferStatus !== 'RECEIVED';
    if (status === 'RELEASED_TO_SHIPMENT') {
      return awaitingReceipt ? 'Shipment planned · awaiting receipt' : 'Shipment planned';
    }
    if (status === 'READY') {
      return awaitingReceipt ? 'Ready for planning · awaiting receipt' : 'Ready for planning';
    }
    if (status === 'WAITING_PREVIOUS_LEG') return 'Waiting for previous leg';
    return leg.planningStatus || '-';
  }

  planningSeverity(leg: CrossDockOrchestrationLeg): 'success' | 'info' | 'warn' | 'secondary' {
    const status = (leg.planningStatus || '').toUpperCase();
    if (status === 'RELEASED_TO_SHIPMENT') return 'success';
    if (status === 'READY') return 'info';
    if (status === 'WAITING_PREVIOUS_LEG') return 'warn';
    return 'secondary';
  }

  receiptLabel(leg: CrossDockOrchestrationLeg): string {
    if (!leg.gatedByTransferId) return 'No inbound receipt gate';
    const status = leg.gatedTransferStatus || 'UNKNOWN';
    return status === 'RECEIVED' ? 'Inbound receipt complete' : `Inbound ${status} · execution blocked`;
  }

  legTitle(leg: CrossDockOrchestrationLeg): string {
    return leg.legSequence ? `Leg ${leg.legSequence}` : 'Leg';
  }

  movementNumber(leg: CrossDockOrchestrationLeg): string {
    return leg.transferNumber || leg.dispatchNumber || '-';
  }

  movementRoute(leg: CrossDockOrchestrationLeg): string {
    return `${leg.originName || 'Origin'} to ${leg.destinationName || 'Destination'}`;
  }

  private metricTone(status?: string): 'success' | 'warn' | 'info' | 'danger' {
    const value = (status || '').toUpperCase();
    if (value.includes('COMPLETE')) return 'success';
    if (value.includes('ERROR') || value.includes('FAIL') || value.includes('BLOCK')) return 'danger';
    if (value.includes('PROGRESS')) return 'info';
    return 'warn';
  }
}
